import { formatarMoeda } from '../formatadores'

export interface ItemParaRateio {
	id: string
	valorBase: number
}

/**
 * cClassTrib reservados a outros tipos de nota, que a Nota de Débito não pode usar (NT 2025.002, regra UB14-60).
 */
export const CCLASSTRIB_PROIBIDOS_NA_NOTA_DE_DEBITO = ['410030', '800001', '800002', '810001', '811001', '811002', '811003']

function emCentavos(valor: number) {
	return Math.round((Number(valor) || 0) * 100)
}

/**
 * Rateia o total entre os itens na proporção do valor base de cada um, em centavos. A sobra do arredondamento vai para
 * o último item com valor base, para a soma bater com o total.
 */
export function ratearValorEntreItens(total: number, itens: ItemParaRateio[]): Record<string, number> {
	const rateio: Record<string, number> = itens.reduce((valores, item) => ({ ...valores, [item.id]: 0 }), {})
	const totalEmCentavos = emCentavos(total)
	const itensComValor = itens.filter(item => emCentavos(item.valorBase) > 0)
	if (totalEmCentavos <= 0 || !itensComValor.length) return rateio

	const somaDasBases = itensComValor.reduce((soma, item) => soma + emCentavos(item.valorBase), 0)
	const centavos: Record<string, number> = {}
	let distribuido = 0
	itensComValor.slice(0, -1).forEach(item => {
		centavos[item.id] = Math.round(totalEmCentavos * emCentavos(item.valorBase) / somaDasBases)
		distribuido += centavos[item.id]
	})

	const ultimo = itensComValor[itensComValor.length - 1]
	centavos[ultimo.id] = totalEmCentavos - distribuido

	// Com o último item muito menor que os outros, o arredondamento dos anteriores pode passar do total: o excesso sai
	// do item de maior valor, que absorve sem ficar negativo
	if (centavos[ultimo.id] < 0) {
		const excesso = -centavos[ultimo.id]
		centavos[ultimo.id] = 0
		const maior = itensComValor.reduce((atual, item) => centavos[item.id] > centavos[atual.id] ? item : atual)
		centavos[maior.id] -= excesso
	}

	Object.keys(centavos).forEach(id => rateio[id] = centavos[id] / 100)
	return rateio
}

export function somarValoresDoDebito(valores: number[]) {
	return valores.reduce((soma, valor) => soma + emCentavos(valor), 0) / 100
}

export function valoresDoDebitoConferem(totalDosItens: number, juros: number, multa: number) {
	return emCentavos(totalDosItens) === emCentavos(juros) + emCentavos(multa)
}

/** Discriminação que abre as informações adicionais da Nota de Débito, seguida do texto livre do operador. */
export function montarInformacoesDaNotaDeDebito(juros: number, multa: number, textoLivre?: string | null) {
	const discriminacao = `Juros: R$ ${formatarMoeda(emCentavos(juros) / 100)}. Multa: R$ ${formatarMoeda(emCentavos(multa) / 100)}.`
	const texto = (textoLivre || '').trim()
	return texto ? `${discriminacao} ${texto}` : discriminacao
}

export function validarCClassTribDaReserva(codigo: string | null | undefined): true | string {
	const cClassTrib = (codigo || '').trim()
	if (!cClassTrib) return 'Informe a classificação tributária de reserva'
	if (CCLASSTRIB_PROIBIDOS_NA_NOTA_DE_DEBITO.includes(cClassTrib)) {
		return `A classificação tributária ${cClassTrib} é reservada a outro tipo de nota e não pode ser usada na Nota de Débito`
	}
	return true
}
